import { useEffect, useRef, useState } from "react";

/**
 * Trailing-edge debounce of a fast-changing value, for inputs that drive a
 * NETWORK request (not just a re-render). Returns `[debounced, pending]`:
 *
 *   - `debounced` follows `value`, but only after `value` has been stable for
 *     `delayMs` — rapid changes (a burst of keystrokes) collapse into a single
 *     update carrying the final value, so the request fires once.
 *   - `pending` is `true` while a change is waiting to be applied
 *     (`debounced !== value`), so the caller can show an early "search queued"
 *     affordance the instant a key is pressed — the feedback stays live even
 *     though the request is held back.
 *
 * Why not `useDeferredValue`: that only DE-PRIORITISES the re-render — the
 * deferred value still catches up within a frame or two, so a value keyed into
 * a `useQuery` still fires a request per keystroke. `useDeferredValue` is right
 * for an in-memory filter (keep typing smooth while a big list re-filters);
 * this hook is right when the value crosses the network.
 *
 * Initialises to `value`, so a value restored from the URL on mount applies
 * immediately with no delay — only subsequent edits are debounced.
 *
 * `onSettle` (optional) runs in the SAME tick the debounced value commits, so a
 * caller can reset state coupled to the query — e.g. paging back to page 0 —
 * atomically with it. Doing that reset in a separate effect keyed on the
 * debounced value would let a dependent effect (a fetch) fire once with the
 * stale coupled state before the reset lands. It fires only on a real settle,
 * never on mount. Read through a ref so a fresh callback identity per render
 * doesn't reschedule the timer.
 */
export function useDebouncedValue(value, delayMs = 500, onSettle) {
  const [debounced, setDebounced] = useState(value);
  const onSettleRef = useRef(onSettle);
  onSettleRef.current = onSettle;

  useEffect(() => {
    // Already settled (mount, or the timer just landed) — nothing to schedule.
    if (value === debounced) return undefined;
    const id = setTimeout(() => {
      setDebounced(value);
      onSettleRef.current?.();
    }, delayMs);
    // Each new value clears the prior timer, so only the last one in a burst
    // survives — the trailing edge.
    return () => clearTimeout(id);
  }, [value, debounced, delayMs]);

  return [debounced, value !== debounced];
}
